import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Bold, Italic, Heading2, Heading3, Quote, List, Link2, Image } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ImagePickerDialog } from '@/components/ImagePickerDialog';

const textTools = [
  { icon: Bold, label: 'Bold', prefix: '**', suffix: '**', placeholder: 'bold text' },
  { icon: Italic, label: 'Italic', prefix: '*', suffix: '*', placeholder: 'italic text' },
  { icon: Heading2, label: 'Heading 2', prefix: '## ', suffix: '', placeholder: 'Heading' },
  { icon: Heading3, label: 'Heading 3', prefix: '### ', suffix: '', placeholder: 'Subheading' },
  { icon: Quote, label: 'Blockquote', prefix: '> ', suffix: '', placeholder: 'quote' },
  { icon: List, label: 'List Item', prefix: '- ', suffix: '', placeholder: 'item' },
  { icon: Link2, label: 'Link', prefix: '[', suffix: '](url)', placeholder: 'link text' },
];

export const MarkdownToolbar = ({ textareaRef, value, onChange }) => {
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  const insertMarkdown = (tool) => {
    const textarea = textareaRef?.current;
    if (!textarea) {
      const newValue = value + tool.prefix + tool.placeholder + tool.suffix;
      onChange(newValue);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.substring(start, end);
    const replacement = tool.prefix + (selected || tool.placeholder) + tool.suffix;
    const newValue = value.substring(0, start) + replacement + value.substring(end);
    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + tool.prefix.length + (selected || tool.placeholder).length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 10);
  };

  const handleImageInsert = (url, altText) => {
    const textarea = textareaRef?.current;
    const markdown = `\n![${altText}](${url})\n`;
    if (!textarea) {
      onChange(value + markdown);
      return;
    }
    const start = textarea.selectionStart;
    const newValue = value.substring(0, start) + markdown + value.substring(start);
    onChange(newValue);
    setTimeout(() => {
      textarea.focus();
      const newPos = start + markdown.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 10);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-0.5 p-1.5 rounded-t-lg bg-[rgba(7,10,10,0.5)] border border-b-0 border-[rgba(201,168,76,0.2)]">
        {textTools.map((tool) => {
          const Icon = tool.icon;
          return (
            <Tooltip key={tool.label}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] hover:bg-[rgba(168,181,178,0.08)]"
                  onClick={() => insertMarkdown(tool)}
                >
                  <Icon className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-xs"
              >
                {tool.label}
              </TooltipContent>
            </Tooltip>
          );
        })}

        {/* Image button - opens picker */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] hover:bg-[rgba(168,181,178,0.08)]"
              onClick={() => setImagePickerOpen(true)}
            >
              <Image className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-xs"
          >
            Insert Image
          </TooltipContent>
        </Tooltip>
      </div>

      <ImagePickerDialog
        open={imagePickerOpen}
        onOpenChange={setImagePickerOpen}
        onSelect={handleImageInsert}
      />
    </TooltipProvider>
  );
};
